import { useIntl } from 'react-intl';
import { type GridColDef } from '@mui/x-data-grid-premium';

import { useIsModuleDisabled, useSettings } from 'app/main/components';
import { ViewTableUtils } from 'app/main/components/page-layouts';

type useOrdersBillingTableColumnsType = () => GridColDef[];

const useOrdersBillingTableColumns: useOrdersBillingTableColumnsType = () => {
	const { sysSettings, t } = useSettings();
	const isModuleDisabled = useIsModuleDisabled();
	const intl = useIntl();

	const columns: GridColDef[] = [
		{
			field: 'inty_title',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_INVOICE_TYPE'),
			minWidth: 150,
			renderCell: ({ value, row }) => ViewTableUtils.renderSquareChip({ value, backgroundColor: row.inty_color })
		},
		{
			field: 'prst_title',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE'),
			minWidth: 200,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderSquareChip({
					value:
						row.orbi_schedule === 'milestone' && value
							? intl.formatMessage(
									{
										id: 'ordersBillingTable.projectPhase',
										defaultMessage: t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE_MILESTONE_VALUE')
									},
									{ value }
								)
							: row.orbi_schedule === 'sign'
								? t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE_SIGNED')
								: row.orbi_d_schedule,
					backgroundColor: row.orbi_schedule === 'milestone' ? row.prst_color : undefined
				})
		},
		{
			field: 'orbi_d_schedule_final',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_SCHEDULE_DATE_FINAL'),
			minWidth: 125,
			renderCell: ({ value }) => (value ? ViewTableUtils.renderDate({ value }) : '--'),
			hideable: false
		},
		{
			field: 'proj_title',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_PROJECT'),
			minWidth: 240,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value, to: { module: 'projects', uid: row.orli_proj_uid } })
		},
		{
			field: 'comp_title',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_COMPANY'),
			minWidth: 240,
			flex: 1,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value, to: { module: 'companies', uid: row.comp_uid } }),
			hideable: false
		},
		{
			field: 'orbi_title',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_DESCRIPTION'),
			minWidth: 400,
			flex: 2,
			renderCell: ViewTableUtils.renderCellWithTooltip
		},
		{
			field: 'orbi_type',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_TYPE'),
			minWidth: 125,
			renderCell: ({ value }) =>
				ViewTableUtils.renderSquareChip({ value: t(`DETAILS_ORDERS_BILLING_FIELD_TYPE_${value}`) })
		},
		{
			field: 'orbi_quantity',
			type: 'number',
			headerName: t('DETAILS_ORDERS_LINES_FIELD_QUANTITY'),
			minWidth: 100,
			renderCell: ({ value }) => (value !== 0 ? value : undefined) || '--'
		},
		{
			field: 'orbi_percentage',
			type: 'number',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_PERCENTAGE'),
			minWidth: 100,
			renderCell: ViewTableUtils.renderPercentage
		},
		{
			field: 'orbi_amount_final',
			type: 'number',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_AMOUNT'),
			minWidth: 125,
			cellClassName: ({ row }) =>
				row.inty_group === 'expenses' ? 'font-semibold text-red-600' : 'font-semibold text-green-700',
			renderCell: ViewTableUtils.renderCurrency,
			hideable: false
		},
		{
			field: 'orbi_billable',
			type: 'boolean',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_BILLABLE'),
			minWidth: 100
		},
		{
			field: 'pate_title',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_PAYMENTTERMS'),
			minWidth: 200,
			renderCell: ({ value, row }) => ViewTableUtils.renderSquareChip({ value, backgroundColor: row.pate_color })
		},
		{
			field: 'pame_title',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_PAYMENTMETHOD'),
			minWidth: 200,
			renderCell: ({ value, row }) => ViewTableUtils.renderSquareChip({ value, backgroundColor: row.pame_color })
		}
	];

	if (sysSettings.invoices_rows_link_lines_enabled === 'true' && !isModuleDisabled('invoices')) {
		columns.push({
			field: 'invc_title',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_INVOICE'),
			minWidth: 150,
			renderCell: ({ value, row }) =>
				ViewTableUtils.renderCellWithTooltip({ value, to: { module: 'invoices', uid: row.invc_uid } })
		});
	} else {
		columns.push({
			field: 'orbi_d_billed',
			headerName: t('DETAILS_ORDERS_BILLING_FIELD_BILLED_DATE'),
			minWidth: 125,
			renderCell: ({ value }) => (value ? ViewTableUtils.renderDate({ value }) : '--')
		});
	}

	return columns;
};

export default useOrdersBillingTableColumns;
